import { supabase } from '../lib/supabase';
import { verificationStorageService } from './storageService';

export const adminService = {
  async isAdmin() {
    if (!supabase) return false;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user?.id) return false;
    const { data } = await supabase
      .from('admin_users')
      .select('user_id')
      .eq('user_id', user.id)
      .maybeSingle();
    return !!data;
  },

  async listUsers(page = 0, pageSize = 20, filters = {}) {
    if (!supabase) return { data: [], total: 0, error: 'Supabase not configured' };
    let q = supabase
      .from('user_profiles')
      .select('id, email, display_name, username, role, city, is_verified, is_blocked, is_dummy, created_at', { count: 'exact' });
    if (filters.role) q = q.eq('role', filters.role);
    if (filters.blocked !== undefined) q = q.eq('is_blocked', !!filters.blocked);
    if (filters.verified !== undefined) q = q.eq('is_verified', !!filters.verified);
    const from = page * pageSize;
    const to = from + pageSize - 1;
    const { data, error, count } = await q.order('created_at', { ascending: false }).range(from, to);
    if (error) return { data: [], total: 0, error: error.message };
    return { data: data || [], total: count ?? 0 };
  },

  async getUser(userId) {
    if (!supabase) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .single();
    if (error) return null;
    return data;
  },

  async getProviderProfile(userId) {
    if (!supabase) return null;
    const { data, error } = await supabase
      .from('service_provider_profiles')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    if (error) return null;
    return data;
  },

  async setBlocked(userId, blocked) {
    if (!supabase) throw new Error('Supabase not configured');
    const { error } = await supabase
      .from('user_profiles')
      .update({ is_blocked: !!blocked, updated_at: new Date().toISOString() })
      .eq('id', userId);
    if (error) throw error;
  },

  async listPendingVerifications() {
    if (!supabase) return [];
    const { data, error } = await supabase
      .from('verification_documents')
      .select(`
        id,
        user_id,
        document_type,
        file_path,
        status,
        created_at,
        user_profiles(display_name, email)
      `)
      .eq('status', 'pending')
      .order('created_at', { ascending: false });
    if (error) return [];
    return data || [];
  },

  async getVerificationDocumentSignedUrl(filePath) {
    const { data } = await verificationStorageService.getSignedUrl(filePath, 300);
    return data?.signedUrl ?? null;
  },

  async approveVerificationDocument(docId) {
    if (!supabase) throw new Error('Supabase not configured');
    const { data: doc, error: fetchErr } = await supabase
      .from('verification_documents')
      .select('user_id')
      .eq('id', docId)
      .single();
    if (fetchErr || !doc?.user_id) throw fetchErr || new Error('Document not found');
    const { data: { user } } = await supabase.auth.getUser();
    const { error: updateDocErr } = await supabase
      .from('verification_documents')
      .update({
        status: 'approved',
        reviewed_at: new Date().toISOString(),
        reviewed_by: user?.id,
      })
      .eq('id', docId);
    if (updateDocErr) throw updateDocErr;
    await supabase
      .from('service_provider_profiles')
      .update({ verification_status: 'verified', updated_at: new Date().toISOString() })
      .eq('user_id', doc.user_id);
    await supabase
      .from('user_profiles')
      .update({ is_verified: true, updated_at: new Date().toISOString() })
      .eq('id', doc.user_id);
  },

  async rejectVerificationDocument(docId) {
    if (!supabase) throw new Error('Supabase not configured');
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase
      .from('verification_documents')
      .update({
        status: 'rejected',
        reviewed_at: new Date().toISOString(),
        reviewed_by: user?.id,
      })
      .eq('id', docId);
    if (error) throw error;
  },

  async createDummyUser(displayName, city, avatarUrl = null) {
    if (!supabase) throw new Error('Supabase not configured');
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('Not authenticated');
    const baseUrl = typeof import.meta !== 'undefined' && import.meta.env?.VITE_SUPABASE_URL;
    if (!baseUrl) throw new Error('VITE_SUPABASE_URL not set');
    const res = await fetch(`${baseUrl}/functions/v1/create-dummy-user`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ display_name: displayName, city: city || '', avatar_url: avatarUrl }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.error || 'Failed to create dummy');
    return json.user_id;
  },

  async listDummyProfiles() {
    if (!supabase) return [];
    const { data, error } = await supabase
      .from('user_profiles')
      .select('id, display_name, username, city, avatar_url, hide_profile, is_dummy')
      .eq('is_dummy', true)
      .order('created_at', { ascending: false });
    if (error) return [];
    return data || [];
  },

  async updateDummyProfile(userId, payload) {
    if (!supabase) throw new Error('Supabase not configured');
    const updates = { updated_at: new Date().toISOString() };
    ['display_name', 'city', 'avatar_url', 'bio', 'hide_profile'].forEach((k) => {
      if (payload[k] !== undefined) updates[k] = payload[k];
    });
    const { error } = await supabase.from('user_profiles').update(updates).eq('id', userId);
    if (error) throw error;
  },

  async listConversationsForUser(userId) {
    if (!supabase) return [];
    const { data: convos, error } = await supabase
      .from('conversations')
      .select('id, participant_one_id, participant_two_id, last_message_at')
      .or(`participant_one_id.eq.${userId},participant_two_id.eq.${userId}`)
      .order('last_message_at', { ascending: false });
    if (error) return [];
    const otherIds = (convos || []).map((c) =>
      c.participant_one_id === userId ? c.participant_two_id : c.participant_one_id
    );
    if (otherIds.length === 0) return [];
    const { data: profiles } = await supabase
      .from('user_profiles')
      .select('id, display_name, username, avatar_url')
      .in('id', otherIds);
    const byId = (profiles || []).reduce((acc, p) => ({ ...acc, [p.id]: p }), {});
    return (convos || []).map((c) => {
      const otherId = c.participant_one_id === userId ? c.participant_two_id : c.participant_one_id;
      return { ...c, other_id: otherId, other_profile: byId[otherId] };
    });
  },

  async sendMessageAsDummy(conversationId, senderId, receiverId, messageText) {
    if (!supabase) throw new Error('Supabase not configured');
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('Not authenticated');
    const baseUrl = typeof import.meta !== 'undefined' && import.meta.env?.VITE_SUPABASE_URL;
    if (!baseUrl) throw new Error('VITE_SUPABASE_URL not set');
    const url = `${baseUrl}/functions/v1/admin-send-message`;
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        conversation_id: conversationId,
        sender_id: senderId,
        receiver_id: receiverId,
        message_text: messageText,
      }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.error || 'Failed to send');
    return json;
  },

  async listBookingsForProvider(providerId) {
    if (!supabase) return [];
    const { data, error } = await supabase
      .from('bookings')
      .select(`
        id, user_id, provider_id, slot, status, amount_paise, created_at,
        user_profiles!bookings_user_id_fkey(id, display_name, username, avatar_url)
      `)
      .eq('provider_id', providerId)
      .order('slot', { ascending: false });
    if (error) return [];
    return data || [];
  },

  async updateBookingStatus(bookingId, status) {
    if (!supabase) throw new Error('Supabase not configured');
    const { error } = await supabase
      .from('bookings')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', bookingId);
    if (error) throw error;
  },

  async listReports() {
    if (!supabase) return [];
    const { data, error } = await supabase
      .from('reports')
      .select(`
        id,
        reporter_id,
        reported_id,
        reason,
        created_at,
        reporter:user_profiles!reports_reporter_id_fkey(display_name, email),
        reported:user_profiles!reports_reported_id_fkey(display_name, email, is_blocked)
      `)
      .order('created_at', { ascending: false });
    if (error) return [];
    return data || [];
  },

  async dismissReport(reportId) {
    if (!supabase) throw new Error('Supabase not configured');
    const { error } = await supabase.from('reports').delete().eq('id', reportId);
    if (error) throw error;
  },
};
